// Narrative client stories used across the public site.

export interface ClientStory {
  id: string;
  name: string;
  location: string;
  occupation: string;
  hook: string;
  body: string[];
  whatWeDid: string;
  outcome: {
    owedBefore: string;
    settledFor: string;
    timeframe: string;
  };
  serviceHref: string;
  topics: Array<
    | "back-taxes"
    | "wage-garnishment"
    | "bank-levy"
    | "offer-in-compromise"
    | "audit"
    | "payroll-tax"
    | "unfiled-returns"
    | "innocent-spouse"
    | "lien"
  >;
}

export const clientStories: ClientStory[] = [
  {
    id: "marcus-garnishment",
    name: "Marcus T.",
    location: "Fort Worth, TX",
    occupation: "Independent contractor",
    hook: "Ignored the first IRS letter. Six months later they took $4,800 from his paycheck.",
    body: [
      "Marcus owed the IRS about $38,000 from two bad years after his divorce. When the first CP14 notice arrived he shoved it in a drawer. \"I'll deal with it next month,\" he told himself.",
      "Next month became next quarter. The letters got thicker and the language got colder. He stopped opening the mail.",
      "Then on a Tuesday morning, his direct deposit hit $1,200 light. The IRS had garnished his wages. His wife found out at the grocery store when the card was declined.",
      "By the time Marcus called us, the IRS had taken $4,800 from three paychecks, filed a federal tax lien against his home, and his original $38,000 balance had ballooned to over $51,000 in penalties and interest.",
    ],
    whatWeDid:
      "Filed a formal Collection Due Process appeal that stopped the garnishment immediately. Prepared and submitted an Offer in Compromise with full financial documentation.",
    outcome: {
      owedBefore: "$51,000",
      settledFor: "$6,200 over 18 months",
      timeframe: "11 days to release; 9 months to settle",
    },
    serviceHref: "/services/irs-collections-defense/wage-garnishment",
    topics: ["wage-garnishment", "offer-in-compromise", "lien"],
  },
  {
    id: "elena-oic",
    name: "Elena R.",
    location: "Plano, TX",
    occupation: "Former small-business owner",
    hook: "$184,000 in back taxes from a failed bakery. She thought bankruptcy was her only option.",
    body: [
      "Elena's bakery closed in 2021 after the lease tripled. What she didn't realize was that the two prior years of payroll taxes had never been fully remitted — and the IRS had assessed the Trust Fund Recovery Penalty personally against her.",
      "By the time she called us she owed $184,260, was being threatened with a bank levy, and had stopped sleeping more than four hours a night.",
      "She had quotes from two national \"tax relief\" companies promising they could settle for \"pennies on the dollar\" — for $9,500 up front, non-refundable.",
    ],
    whatWeDid:
      "Pulled her IRS transcripts, filed three missing returns, documented hardship under the IRS Reasonable Collection Potential formula, and prepared a thoroughly supported Offer in Compromise.",
    outcome: {
      owedBefore: "$184,260",
      settledFor: "$12,400",
      timeframe: "9 months to acceptance",
    },
    serviceHref: "/services/tax-debt-relief/offer-in-compromise",
    topics: ["offer-in-compromise", "back-taxes", "payroll-tax"],
  },
  {
    id: "jason-audit",
    name: "Jason M.",
    location: "Dallas, TX",
    occupation: "Software consultant",
    hook: "An IRS audit letter for a $74,000 proposed assessment. He almost just signed and paid.",
    body: [
      "Jason got the dreaded 4549 letter — the IRS's proposed adjustment from a correspondence audit. The number at the bottom was $74,118.",
      "His prior CPA had retired. He almost signed the form just to make it go away. A friend told him to call us first.",
      "On review, the auditor had disallowed every Schedule C expense without a substantiation request, ignored two 1099-K corrections, and miscalculated self-employment tax.",
    ],
    whatWeDid:
      "Filed a formal Power of Attorney, requested the full audit file, reconstructed substantiation from bank records, and walked the auditor's manager through every disputed item.",
    outcome: {
      owedBefore: "$74,118 (proposed)",
      settledFor: "$3,940 (final)",
      timeframe: "4 months from POA to closure",
    },
    serviceHref: "/services/audit-defense/irs-audit-representation",
    topics: ["audit", "unfiled-returns"],
  },
  {
    id: "linda-innocent-spouse",
    name: "Linda K.",
    location: "Frisco, TX",
    occupation: "Registered nurse",
    hook: "Her ex-husband under-reported $120,000. The IRS came after her paycheck.",
    body: [
      "Linda filed jointly with her ex for seven years. She signed where he told her to sign. After the divorce, the IRS audited his consulting income and found $120,000 in unreported revenue — and assessed both of them jointly.",
      "She made $68,000 a year as a nurse. The IRS garnished her wages for a tax bill that wasn't hers.",
    ],
    whatWeDid:
      "Filed Form 8857 for Innocent Spouse Relief with sworn statements, the divorce decree, and bank records proving she never received the unreported income. Requested a parallel collection hold.",
    outcome: {
      owedBefore: "$148,000 (joint assessment)",
      settledFor: "$0 (full innocent spouse relief)",
      timeframe: "13 months",
    },
    serviceHref: "/services/tax-debt-relief/innocent-spouse-relief",
    topics: ["innocent-spouse", "wage-garnishment"],
  },
];

export const getStoryByTopic = (topic: ClientStory["topics"][number]) =>
  clientStories.find((s) => s.topics.includes(topic));
